import React, { ReactNode } from "react";
import Link from "next/link";
import { cn } from "utils/utils";
import { ArrowRight } from "lucide-react";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { TradeCountBadge } from "modules/common/components/mainbar/TradeCountBadge";

interface TradeSectionTitleProps {
  currentCount?: number;
  total?: number;
  title?: string;
  /** Short description displayed below the title on desktop */
  description?: string;
  className?: string;
  type?: "wants" | "trades" | "match";
  viewAllLink?: string;
  /** Optional content to render on the left side (e.g., mobile filter bar) */
  leftContent?: ReactNode;
  /** Show skeleton state for count badge */
  isLoading?: boolean;
  /** When true, the title sticks below the AppBar on scroll */
  sticky?: boolean;
  /** When true, the count is rendered by the page's mainbar instead, so
   *  this row's own badge collapses on desktop (still shown on mobile). */
  countInMainbar?: boolean;
}

function TradeSectionTitle({
  currentCount,
  total,
  title,
  description,
  className,
  type,
  viewAllLink,
  leftContent,
  isLoading = false,
  sticky = false,
  countInMainbar = false,
}: TradeSectionTitleProps) {
  // `total` is optional: sources without a true total (matched-with-user)
  // pass only currentCount and the badge renders it bare.
  const hasValidCounts = currentCount !== undefined;

  return (
    <div
      className={cn(
        "flex w-full items-center justify-between py-2",
        sticky &&
          "bg-base-300/90 rounded-b-box sticky top-16 z-30 px-3 backdrop-blur-md",
        countInMainbar && "md:hidden",
        className
      )}
    >
      {/* Left side: title and/or leftContent (e.g., mobile filter bar) */}
      {/* leftContent is always rendered to preserve component state (e.g. modal open/close) */}
      {title && leftContent ? (
        <>
          <div className="min-w-0 flex-1 md:hidden">{leftContent}</div>
          <div className="hidden md:block">
            <h2 className="text-base-content flex items-center gap-3 text-balance text-lg font-semibold md:text-xl">
              {title}
            </h2>
            {description && (
              <p className="text-base-content/60 text-sm">{description}</p>
            )}
          </div>
        </>
      ) : title ? (
        <div>
          <h2 className="text-base-content flex items-start gap-3 text-balance text-lg font-semibold md:items-center md:text-xl">
            {title}
          </h2>
          {description && (
            <p className="text-base-content/60 hidden text-sm md:block">
              {description}
            </p>
          )}
        </div>
      ) : leftContent ? (
        <div className="min-w-0 flex-1">{leftContent}</div>
      ) : isLoading ? (
        <div className="no-scrollbar flex gap-2 overflow-x-auto">
          <div className="skeleton btn btn-sm btn-soft border-base-300 h-8 w-20 rounded-full border" />
          <div className="skeleton btn btn-sm btn-soft border-base-300 h-8 w-16 rounded-full border" />
        </div>
      ) : null}

      <div className="ml-auto flex shrink-0 items-center gap-1 px-1">
        {isLoading ? (
          <TradeCountBadge currentCount={0} total={0} type={type} isLoading />
        ) : (
          hasValidCounts && (
            <TradeCountBadge
              currentCount={currentCount}
              total={total}
              type={type}
            />
          )
        )}
        {viewAllLink && (
          <Link
            href={viewAllLink}
            className="btn btn-soft btn-xs btn-primary flex gap-1"
          >
            View All
            <ArrowRight size={ICON_SIZE} />
          </Link>
        )}
      </div>
    </div>
  );
}

export default TradeSectionTitle;
